import { localizeServiceName } from '@/utils/serviceNames';
import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { Service } from '@/data/types';
import { spacing, type, useAppTheme } from '@/theme';
import { ServiceCard } from './ServiceCard';
import { ScaledSheet } from '@/theme/responsive';

/**
 * A shop's (or independent stylist's) services grouped under their category headings, in the order
 * categories first appear, with "Other" last. Every new service now has to go in a category, so
 * this matches how the stylist organised their menu.
 */
export function ServicesByCategory({ services }: { services: Service[] }) {
  const { colors } = useAppTheme();
  const groups = useMemo(() => {
    const byName = new Map<string, Service[]>();
    for (const service of services) {
      const name = service.category || 'Other';
      byName.set(name, [...(byName.get(name) ?? []), service]);
    }
    return [...byName.entries()].sort(([a], [b]) => (a === 'Other' ? 1 : b === 'Other' ? -1 : 0));
  }, [services]);

  return (
    <View style={styles.groups}>
      {groups.map(([name, items]) => (
        <View key={name} style={styles.group}>
          <View style={styles.heading}>
            <Text style={[type.h3, { color: colors.textPrimary, flexShrink: 1 }]}>{localizeServiceName(name)}</Text>
            <Text style={[type.caption, { color: colors.textTertiary }]}>
              {items.length} {items.length === 1 ? 'service' : 'services'}
            </Text>
          </View>
          {items.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </View>
      ))}
    </View>
  );
}

const styles = ScaledSheet.create({
  groups: { gap: spacing.xl },
  group: { gap: spacing.md },
  heading: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' },
});
